import React from "react";
import { DURATION_OPTIONS, PLATFORMS } from "../../constants/constants";
import {
  AssigneeDropdown,
  MultiSelectAssigneeDropdown,
  CustomDateInput,
  CustomTimeInput,
  parseTimeString,
  formatToHHMM
} from "./Task.utils";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import MultiSelect from "../../components/common/MultiSelect";
import { PLATFORM_CONFIG } from "../../utils/platformConfigFields";

export const TaskOptionsDropdown = ({
  options = [],
  value = "",
  onChange,
  disabled,
  type,
  size = "sm",
}) => {
  if (!options.length) {
    return (
      <span className="text-muted">
        {size === "modal" ? "No options available" : "—"}
      </span>
    );
  }

  if (type === "multiselect") {
    const formattedOptions = options.map((opt) => ({
      value: opt.key,
      label: opt.value,
    }));

    return (
      <MultiSelect
        options={formattedOptions}
        value={value || []}
        onChange={onChange}
        placeholder="Select..."
        avatarMode={true}
        disabled={disabled}
      />
    );
  }

  return (
    <select
      className="form-select form-select-sm"
      style={
        size === "sm"
          ? { minWidth: "90px", height: "38px", fontSize: "12px" }
          : undefined
      }
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
    >
      <option value="">— Select —</option>
      {options.map((opt) => (
        <option key={opt.key} value={opt.key}>
          {opt.value}
        </option>
      ))}
    </select>
  );
};

export const renderTaskFieldValue = ({
  col,
  action,
  platformType,
  taskOptions = {},
  participants,
  onUpdate,
  isLoading,
  size = "sm",
}) => {
  const config = PLATFORM_CONFIG[platformType] || {};
  const value = action?.[col.key];

  switch (col.key) {
    case "assignee":
      if (config.assigneeType === "none") {
        return <span className="text-muted">—</span>;
      }
      if (config.assigneeType === "single") {
        return (
          <AssigneeDropdown
            participants={participants}
            action={action}
            onAssigneeChange={(email) => onUpdate({ email: [email] })}
          />
        );
      }
      return (
        <MultiSelectAssigneeDropdown
          participants={participants}
          action={action}
          onAssigneeChange={(values) =>
            onUpdate({ email: Array.isArray(values) ? values : [values] })
          }
        />
      );

    case "date":
    case "due_on":
      return (
        <DatePicker
          selected={value ? new Date(value) : null}
          onChange={(date) => onUpdate({ [col.key]: date?.toISOString() })}
          minDate={new Date()}
          dateFormat="MMM dd, yyyy"
          customInput={<CustomDateInput placeholder="Select Date" />}
        />
      );

    case "start_time":
      return (
        <DatePicker
          selected={parseTimeString(value)}
          onChange={(date) => onUpdate({ startTime: formatToHHMM(date) })}
          showTimeSelect
          showTimeSelectOnly
          timeIntervals={15}
          dateFormat="hh:mm aa"
          customInput={<CustomTimeInput placeholder="Time" />}
        />
      );

    case "duration":
      return (
        <select
          className="form-select form-select-sm w-auto"
          style={
            size === "sm"
              ? { minWidth: "90px", height: "32px", fontSize: "12px" }
              : undefined
          }
          value={value || ""}
          onChange={(e) => onUpdate({ duration: e.target.value })}
        >
          <option value="">—</option>
          {DURATION_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );

    case "priority": {
      if (platformType === PLATFORMS.TODOIST) {
        return (
          <select
            className="form-select form-select-sm"
            value={value || ""}
            onChange={(e) => onUpdate({ priority: e.target.value })}
          >
            <option value="">—</option>
            <option value="1">P1</option>
            <option value="2">P2</option>
            <option value="3">P3</option>
            <option value="4">P4</option>
          </select>
        );
      }
      return (
        <TaskOptionsDropdown
          options={taskOptions[col.key] || []}
          value={value || ""}
          disabled={isLoading}
          type={col.type}
          size={size}
          onChange={(selectedKey) => onUpdate({ [col.key]: selectedKey })}
        />
      );
    }

    case "labels":
    case "tags":
    case "list":
    case "properties":
    case "milestone":
    case "sprint":
      return (
        <TaskOptionsDropdown
          options={taskOptions[col.key] || []}
          value={value || ""}
          disabled={isLoading}
          type={col.type}
          size={size}
          onChange={(selectedKey) => onUpdate({ [col.key]: selectedKey })}
        />
      );

    case "permission":
      return (
        <select
          className="form-select form-select-sm"
          style={
            size === "sm"
              ? { minWidth: "90px", height: "38px", fontSize: "12px" }
              : undefined
          }
          value={value || ""}
          onChange={(e) => onUpdate({ permission: e.target.value })}
        >
          <option value="">—</option>
          <option value="send">Send Mail</option>
          <option value="draft">Save Draft</option>
        </select>
      );

    default:
      return <span className="text-muted">—</span>;
  }
};